import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Zap, Briefcase, GraduationCap, Heart, Activity, DollarSign, FileText, Clock, MessageCircle, Sparkles, Send, HelpCircle, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Briefcase, GraduationCap, Heart, Activity, DollarSign, FileText, Clock
};

export default function NewDecisionPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [modes, setModes] = useState({});
  const [selectedMode, setSelectedMode] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [optionA, setOptionA] = useState('');
  const [optionB, setOptionB] = useState('');
  const [formData, setFormData] = useState({});
  const [riskTolerance, setRiskTolerance] = useState([5]);
  const [timeHorizon, setTimeHorizon] = useState('3_years');
  const [loading, setLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatFieldContext, setChatFieldContext] = useState('');

  // Mode-specific placeholders so each mode shows relevant examples
  const modePlaceholders = {
    career: {
      title: "e.g., Should I switch from consulting to product management?",
      description: "I've been working at EY for 3.5 years in consulting. I'm considering switching to tech as either a Product Manager or Data Scientist. PM would value my existing experience but DS pays more long-term...",
      optionA: "e.g., Switch to Product Manager role",
      optionB: "e.g., Switch to Data Scientist role"
    },
    education: {
      title: "e.g., Should I pursue an MBA or a Data Science certification?",
      description: "I have a bachelor's degree and 4 years of work experience. I'm considering going back to school to advance my career. An MBA would broaden my options but costs more, while a bootcamp is faster and cheaper...",
      optionA: "e.g., Enroll in MBA program",
      optionB: "e.g., Take a Data Science bootcamp"
    },
    relationship: {
      title: "e.g., Should I move in with my partner or maintain separate spaces?",
      description: "We've been dating for 2 years. Things are good but we disagree on future plans like where to live and when to settle down. Moving in would test compatibility but also adds pressure...",
      optionA: "e.g., Move in together",
      optionB: "e.g., Keep living separately for now"
    },
    health: {
      title: "e.g., Should I commit to a strict fitness routine or a gradual lifestyle change?",
      description: "I've been sedentary for years, gaining weight, poor sleep. My doctor recommended changes but I'm not sure how aggressively to approach it. I've tried and failed before with extreme diets...",
      optionA: "e.g., Join a gym with a personal trainer",
      optionB: "e.g., Start with daily walks and home workouts"
    },
    financial: {
      title: "e.g., Should I invest my savings in stocks or buy real estate?",
      description: "I have 10L saved up and I'm trying to decide the best way to grow my wealth over the next 5 years. Stocks are more liquid but volatile. Real estate is stable but locks up my capital...",
      optionA: "e.g., Invest in index funds/SIPs",
      optionB: "e.g., Make a down payment on property"
    },
    custom: {
      title: "e.g., Should I relocate to a new city for a fresh start?",
      description: "Describe the full context of your decision. What led to this point? What are the stakes?",
      optionA: "e.g., First choice you're considering",
      optionB: "e.g., Alternative choice"
    },
    daily: {
      title: "e.g., Should I switch to waking up at 5 AM?",
      description: "I currently wake up at 8 AM and feel rushed every morning. I'm considering an early morning routine to be more productive but I'm worried I can't sustain it...",
      optionA: "e.g., Wake up at 5 AM daily",
      optionB: "e.g., Keep current schedule but optimize it"
    }
  };

  const getPlaceholder = (field) => {
    const mp = modePlaceholders[selectedMode] || modePlaceholders.custom;
    return mp[field] || '';
  };

  const fetchModes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/decisions/modes`, { headers: getAuthHeaders(), withCredentials: true });
      setModes(res.data);
    } catch (err) {
      toast.error('Failed to load decision modes');
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchModes();
  }, [fetchModes]);

  const currentMode = modes[selectedMode] || null;

  const openChatFor = (fieldName) => {
    setChatFieldContext(fieldName);
    setChatOpen(true);
    setChatMessages([{
      role: 'assistant',
      content: `I'm here to help you with "${fieldName.replace(/_/g, ' ')}". Just tell me about your situation in any language, even broken sentences - I'll help you articulate it clearly.`
    }]);
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
    setChatLoading(true);
    try {
      const res = await axios.post(`${API}/chat/help`, {
        message: msg,
        context: JSON.stringify({ title, description, formData }),
        decision_mode: selectedMode,
        field_name: chatFieldContext
      }, { headers: getAuthHeaders(), withCredentials: true });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I had trouble processing that. Try rephrasing?' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const applySuggestion = (text) => {
    const cleanText = text.replace(/^["']|["']$/g, '').trim();
    if (chatFieldContext === 'description') setDescription(cleanText);
    else if (chatFieldContext === 'option_a') setOptionA(cleanText);
    else if (chatFieldContext === 'option_b') setOptionB(cleanText);
    else if (chatFieldContext === 'title') setTitle(cleanText);
    else setFormData(prev => ({ ...prev, [chatFieldContext]: cleanText }));
    toast.success('Text applied!');
    setChatOpen(false);
  };

  const handleSubmit = async () => {
    if (!title || !description || !optionA || !optionB) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const decRes = await axios.post(`${API}/decisions`, {
        mode: selectedMode,
        title, description,
        option_a: optionA,
        option_b: optionB,
        form_data: formData,
        risk_tolerance: riskTolerance[0],
        time_horizon: timeHorizon
      }, { headers, withCredentials: true });
      
      toast.info('Generating your simulation... This may take 30-60 seconds.');
      
      const simRes = await axios.post(`${API}/simulations/generate`, {
        decision_id: decRes.data.decision_id
      }, { headers, withCredentials: true });
      
      toast.success('Simulation complete!');
      navigate(`/simulation/${decRes.data.decision_id}`);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('No simulations remaining. Purchase a Decision Pack to continue.');
        navigate('/upgrade');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to generate simulation');
      }
    } finally {
      setLoading(false);
    }
  };

  const timeHorizons = [
    { value: '6_months', label: '6 Months' },
    { value: '1_year', label: '1 Year' },
    { value: '3_years', label: '3 Years' },
    { value: '5_years', label: '5 Years' },
    { value: '10_years', label: '10 Years' },
  ];

  const riskLabels = ['Very Cautious', 'Cautious', 'Moderate', 'Bold', 'Very Bold'];
  const getRiskLabel = (v) => riskLabels[Math.min(Math.floor((v - 1) / 2), 4)];

  const renderField = (field) => {
    const val = formData[field.name] || '';
    const setVal = (v) => setFormData(prev => ({ ...prev, [field.name]: v }));
    
    return (
      <div key={field.name} className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-widest" style={{ color: '#475569' }}>
            {field.label} {field.required && <span style={{ color: '#EF4444' }}>*</span>}
          </Label>
          <button onClick={() => openChatFor(field.name)} className="opacity-50 hover:opacity-100 transition-opacity" data-testid={`help-${field.name}`}>
            <MessageCircle className="w-3.5 h-3.5" style={{ color: '#6366F1' }} />
          </button>
        </div>
        {field.type === 'textarea' ? (
          <Textarea data-testid={`field-${field.name}`} value={val} onChange={e => setVal(e.target.value)} placeholder={field.placeholder}
            className="min-h-[80px] text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
        ) : field.type === 'select' ? (
          <Select value={val} onValueChange={setVal}>
            <SelectTrigger data-testid={`field-${field.name}`} className="text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: val ? '#F8FAFC' : '#475569' }}>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent style={{ background: '#0F1014', border: '1px solid rgba(255,255,255,0.1)' }}>
              {field.options?.map(opt => (
                <SelectItem key={opt} value={opt} className="text-sm" style={{ color: '#F8FAFC' }}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input data-testid={`field-${field.name}`} type={field.type || 'text'} value={val} onChange={e => setVal(e.target.value)} placeholder={field.placeholder}
            className="text-sm py-5" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      {/* Nav */}
      <nav className="glass-heavy sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#6366F1' }}>
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Syne' }}>Regret Engine</span>
          </div>
          <Button data-testid="back-from-decision" onClick={() => navigate('/dashboard')} variant="ghost" className="text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </nav>

      {/* Steps indicator */}
      <div className="max-w-4xl mx-auto px-6 pt-8">
        <div className="flex items-center gap-2 mb-8" data-testid="step-indicator">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors"
                style={{ background: step >= s ? '#6366F1' : 'rgba(255,255,255,0.05)', color: step >= s ? '#fff' : '#475569' }}>
                {s}
              </div>
              {s < 4 && <div className="w-12 h-0.5 transition-colors" style={{ background: step > s ? '#6366F1' : 'rgba(255,255,255,0.1)' }} />}
            </div>
          ))}
          <span className="ml-4 text-xs" style={{ color: '#94A3B8' }}>
            {step === 1 && 'Choose Mode'}{step === 2 && 'Describe Decision'}{step === 3 && 'Mode-Specific Details'}{step === 4 && 'Configure & Generate'}
          </span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 pb-20">
        {/* Step 1: Choose Mode */}
        {step === 1 && (
          <div className="animate-fadeIn" data-testid="step-1">
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>What kind of decision are you facing?</h2>
            <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>Each mode tailors the analysis to your specific situation.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(modes).map(([key, mode]) => {
                const Icon = iconMap[mode.icon] || FileText;
                return (
                  <button key={key} onClick={() => { setSelectedMode(key); setFormData({}); setStep(2); }}
                    className={`glass-card rounded-xl p-6 text-left transition-all hover:border-white/10 ${selectedMode === key ? 'ring-2' : ''}`}
                    style={selectedMode === key ? { borderColor: 'rgba(99,102,241,0.5)', boxShadow: '0 0 15px rgba(99,102,241,0.2)' } : {}}
                    data-testid={`mode-${key}`}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(99,102,241,0.15)' }}>
                      <Icon className="w-5 h-5" style={{ color: '#6366F1' }} />
                    </div>
                    <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Syne' }}>{mode.label}</h3>
                    <p className="text-xs" style={{ color: '#94A3B8' }}>{mode.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Core Decision Description */}
        {step === 2 && (
          <div className="animate-fadeIn" data-testid="step-2">
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>Describe Your Decision</h2>
            <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
              Tell us what you're deciding. Confused? Click the chat icon next to any field for AI help.
            </p>
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-widest" style={{ color: '#475569' }}>Decision Title <span style={{ color: '#EF4444' }}>*</span></Label>
                  <button onClick={() => openChatFor('title')} className="opacity-50 hover:opacity-100" data-testid="help-title">
                    <MessageCircle className="w-3.5 h-3.5" style={{ color: '#6366F1' }} />
                  </button>
                </div>
                <Input data-testid="decision-title" value={title} onChange={e => setTitle(e.target.value)} placeholder={getPlaceholder('title')}
                  className="py-5 text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-widest" style={{ color: '#475569' }}>Describe Your Situation <span style={{ color: '#EF4444' }}>*</span></Label>
                  <button onClick={() => openChatFor('description')} className="opacity-50 hover:opacity-100" data-testid="help-description">
                    <MessageCircle className="w-3.5 h-3.5" style={{ color: '#6366F1' }} />
                  </button>
                </div>
                <Textarea data-testid="decision-description" value={description} onChange={e => setDescription(e.target.value)}
                  placeholder={getPlaceholder('description')}
                  className="min-h-[120px] text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs uppercase tracking-widest" style={{ color: '#475569' }}>Option A <span style={{ color: '#EF4444' }}>*</span></Label>
                    <button onClick={() => openChatFor('option_a')} className="opacity-50 hover:opacity-100" data-testid="help-option-a">
                      <MessageCircle className="w-3.5 h-3.5" style={{ color: '#6366F1' }} />
                    </button>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger><HelpCircle className="w-3.5 h-3.5" style={{ color: '#475569' }} /></TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs" style={{ background: '#0F1014', borderColor: 'rgba(255,255,255,0.1)' }}>
                          <p>This is the first choice you're considering. Be specific: "Take the new job at Google" is better than "Change jobs".</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Input data-testid="decision-option-a" value={optionA} onChange={e => setOptionA(e.target.value)} placeholder={getPlaceholder('optionA')}
                    className="py-5 text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs uppercase tracking-widest" style={{ color: '#475569' }}>Option B <span style={{ color: '#EF4444' }}>*</span></Label>
                    <button onClick={() => openChatFor('option_b')} className="opacity-50 hover:opacity-100" data-testid="help-option-b">
                      <MessageCircle className="w-3.5 h-3.5" style={{ color: '#6366F1' }} />
                    </button>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger><HelpCircle className="w-3.5 h-3.5" style={{ color: '#475569' }} /></TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs" style={{ background: '#0F1014', borderColor: 'rgba(255,255,255,0.1)' }}>
                          <p>This is the alternative you're weighing against Option A. What's the other path you could take?</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Input data-testid="decision-option-b" value={optionB} onChange={e => setOptionB(e.target.value)} placeholder={getPlaceholder('optionB')}
                    className="py-5 text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
                </div>
              </div>
            </div>
            <div className="flex justify-between mt-10">
              <Button data-testid="step2-back" variant="ghost" onClick={() => setStep(1)} className="text-sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button data-testid="step2-next" onClick={() => { if (!title || !description || !optionA || !optionB) { toast.error('Please fill all required fields'); return; } setStep(3); }}
                className="rounded-full px-6 text-sm" style={{ background: '#6366F1' }}>
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Mode-Specific Fields */}
        {step === 3 && currentMode && (
          <div className="animate-fadeIn" data-testid="step-3">
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>
              {currentMode.label} Details
            </h2>
            <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
              These details help the AI create more specific and useful scenarios. Click the chat icon for AI help.
            </p>
            <div className="space-y-6">
              {currentMode.fields.map(field => renderField(field))}
            </div>
            <div className="flex justify-between mt-10">
              <Button data-testid="step3-back" variant="ghost" onClick={() => setStep(2)} className="text-sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button data-testid="step3-next" onClick={() => setStep(4)}
                className="rounded-full px-6 text-sm" style={{ background: '#6366F1' }}>
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Risk Tolerance & Time Horizon + Generate */}
        {step === 4 && (
          <div className="animate-fadeIn" data-testid="step-4">
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>Configure Your Simulation</h2>
            <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
              These settings shape how the AI weighs outcomes. Understanding them helps you get the most useful results.
            </p>

            <div className="space-y-10">
              {/* Risk Tolerance */}
              <div className="glass-card rounded-xl p-8">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Syne' }}>Risk Tolerance</h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger><HelpCircle className="w-4 h-4" style={{ color: '#475569' }} /></TooltipTrigger>
                      <TooltipContent className="max-w-sm text-xs leading-relaxed" style={{ background: '#0F1014', borderColor: 'rgba(255,255,255,0.1)' }}>
                        <p className="font-semibold mb-1">How This Affects Results:</p>
                        <p>Lower risk tolerance = the simulation weighs stability, safety, and loss-avoidance more heavily. Higher risk tolerance = the simulation explores bold outcomes and weighs potential upside more.</p>
                        <p className="mt-2">Set this based on your actual comfort with uncertainty, not what you think is "right".</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-xs mb-6" style={{ color: '#94A3B8' }}>
                  This tells the AI how to weigh risky vs safe outcomes. Low = prioritize stability. High = explore bold possibilities. 
                  <strong> Your setting: {riskTolerance[0]}/10 ({getRiskLabel(riskTolerance[0])})</strong>
                </p>
                <Slider data-testid="risk-slider" value={riskTolerance} onValueChange={setRiskTolerance} min={1} max={10} step={1} className="mb-4" />
                <div className="flex justify-between text-xs" style={{ color: '#475569' }}>
                  <span>Very Cautious</span>
                  <span>Balanced</span>
                  <span>Very Bold</span>
                </div>
              </div>

              {/* Time Horizon */}
              <div className="glass-card rounded-xl p-8">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Syne' }}>Simulation Time Horizon</h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger><HelpCircle className="w-4 h-4" style={{ color: '#475569' }} /></TooltipTrigger>
                      <TooltipContent className="max-w-sm text-xs leading-relaxed" style={{ background: '#0F1014', borderColor: 'rgba(255,255,255,0.1)' }}>
                        <p className="font-semibold mb-1">How This Affects Results:</p>
                        <p>Shorter horizons (6 months) focus on immediate emotional and practical impact. Longer horizons (5-10 years) reveal compounding effects, lifestyle changes, and long-term regret patterns.</p>
                        <p className="mt-2">Career decisions often benefit from 3-5 year horizons. Daily routine changes work well with 6-12 months.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-xs mb-6" style={{ color: '#94A3B8' }}>
                  How far into the future should the AI simulate? Short horizons show immediate impact. Longer ones reveal compounding effects.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {timeHorizons.map(th => (
                    <button key={th.value} onClick={() => setTimeHorizon(th.value)}
                      className="rounded-lg p-3 text-sm font-medium transition-all text-center"
                      style={{
                        background: timeHorizon === th.value ? '#6366F1' : 'rgba(255,255,255,0.05)',
                        color: timeHorizon === th.value ? '#fff' : '#94A3B8',
                        border: `1px solid ${timeHorizon === th.value ? '#6366F1' : 'rgba(255,255,255,0.1)'}`
                      }}
                      data-testid={`horizon-${th.value}`}>
                      {th.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="glass-card rounded-xl p-8">
                <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Syne' }}>Summary</h3>
                <div className="space-y-3 text-sm" style={{ color: '#94A3B8' }}>
                  <p><span style={{ color: '#475569' }}>Mode:</span> {currentMode?.label}</p>
                  <p><span style={{ color: '#475569' }}>Decision:</span> {title}</p>
                  <p><span style={{ color: '#475569' }}>Option A:</span> {optionA}</p>
                  <p><span style={{ color: '#475569' }}>Option B:</span> {optionB}</p>
                  <p><span style={{ color: '#475569' }}>Risk:</span> {riskTolerance[0]}/10 ({getRiskLabel(riskTolerance[0])})</p>
                  <p><span style={{ color: '#475569' }}>Horizon:</span> {timeHorizons.find(t => t.value === timeHorizon)?.label}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-between mt-10">
              <Button data-testid="step4-back" variant="ghost" onClick={() => setStep(3)} className="text-sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button data-testid="generate-simulation" onClick={handleSubmit} disabled={loading}
                className="rounded-full px-8 py-6 text-base font-bold transition-transform hover:-translate-y-1"
                style={{ background: '#6366F1', boxShadow: '0 0 20px rgba(99,102,241,0.4)' }}>
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating Simulation...</> : <><Sparkles className="w-5 h-5 mr-2" /> Generate Simulation</>}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* AI Chat Helper Overlay */}
      {chatOpen && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4" data-testid="chat-overlay">
          <div className="absolute inset-0 bg-black/60" onClick={() => setChatOpen(false)} />
          <div className="relative w-full max-w-md glass-heavy rounded-2xl overflow-hidden flex flex-col" style={{ maxHeight: '70vh' }}>
            <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} />
                <span className="text-sm font-semibold" style={{ fontFamily: 'Syne' }}>AI Helper</span>
              </div>
              <button onClick={() => setChatOpen(false)} data-testid="close-chat"><X className="w-4 h-4" style={{ color: '#94A3B8' }} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: '200px' }}>
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-xl p-3 text-sm ${msg.role === 'user' ? '' : ''}`}
                    style={{ background: msg.role === 'user' ? '#6366F1' : 'rgba(255,255,255,0.05)', color: '#F8FAFC' }}>
                    {msg.content}
                    {msg.role === 'assistant' && (
                      <button onClick={() => applySuggestion(msg.content)} className="mt-2 text-xs underline block opacity-70 hover:opacity-100" data-testid={`use-suggestion-${i}`}>
                        Use this text
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#6366F1' }} />
                  </div>
                </div>
              )}
            </div>
            <div className="p-3 flex gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <Input data-testid="chat-input" value={chatInput} onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendChat()} placeholder="Describe your situation..."
                className="text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
              <Button data-testid="send-chat" onClick={sendChat} disabled={chatLoading} className="px-3" style={{ background: '#6366F1' }}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
